import { useKeycloak } from "@react-keycloak/web";
import { useNavigate } from "react-router";
import { DashboardFilters } from "../components/DashboardFilters";
import { DashboardList } from "../components/DashboardList";
import { getAllProjects, restoreProject } from "../api/projects";
import { PROJECT_SORT_FIELDS, SortParam } from "../types/pagination";

/**
 * Converts a stored archived-list sort key such as `updated_at_desc` into the backend `sort` format.
 * Unknown keys fall back to the default (most recently updated first).
 * @param {string} value - sort key, `<field>_<asc|desc>`
 * @returns {SortParam[]} backend sort parameters
 */
const parseArchivedSort = (value: string): SortParam[] => {
  const match = /^(.+)_(asc|desc)$/.exec(value);
  const field = match?.[1];
  if (!match || !field || !(PROJECT_SORT_FIELDS as readonly string[]).includes(field)) {
    return [{ field: 'updated_at', direction: 'desc' }];
  }
  return [{ field, direction: match[2] as SortParam['direction'] }];
};
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { CloneProjectModal } from "../components/CloneProjectModal";
import { useMyAccess } from "../hooks/useMyAccess";
import { canOwnProjects } from "../helpers/projectPermissions";
import { FaLock } from "react-icons/fa";
import { useProjectsPage } from "../state/projects";

export const ArchivedProjectsPage = (
  { showMenu, setShowMenu }:
    { showMenu: boolean, setShowMenu?: React.Dispatch<React.SetStateAction<boolean>> }
) => {

  const { keycloak: { token } } = useKeycloak();
  const navigate = useNavigate();
  const { myAccess, loading: accessLoading } = useMyAccess();
  const hasAccess = canOwnProjects(myAccess);
  const { refreshProjects } = useProjectsPage();
  // Archived projects are kept in local state on purpose so they never leak into
  // useProjectsStore (the sidebar / active /projects list read from that store).
  const [projectsData, setProjectsData] = useState<ProjectInterface[]>([]);
  const [sortOrder, setSortOrder] = useState<string>(() =>
    localStorage.getItem('archived_projects_sortOrder') || 'updated_at_desc'
  );
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [restoreTarget, setRestoreTarget] = useState<ProjectInterface | null>(null);
  const [isRestoring, setIsRestoring] = useState<boolean>(false);
  const [cloneTarget, setCloneTarget] = useState<ProjectInterface | null>(null);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState<boolean>(false);

  const loadProjects = async (sort: string, risk: string = riskLevelFilter) => {
    if (!token) return;
    try {
      setIsLoading(true);
      setLoadError(false);
      const data = await getAllProjects({
        archived: true,
        sort: parseArchivedSort(sort),
        risk: risk !== 'all' ? risk : undefined,
      });
      setProjectsData(data);
    } catch (error) {
      setLoadError(true);
      toast.error('Failed to load archived projects', {
        toastId: 'failed-to-load-archived-projects'
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token && !accessLoading && hasAccess) {
      loadProjects(sortOrder);
    }
  }, [token, accessLoading, hasAccess]);

  const handleSortChange = async (value: string) => {
    setSortOrder(value);
    localStorage.setItem('archived_projects_sortOrder', value);
    await loadProjects(value);
  };

  const handleRiskFilterChange = async (value: string) => {
    setRiskLevelFilter(value);
    await loadProjects(sortOrder, value);
  };

  const handleRestore = async () => {
    if (!restoreTarget || !token || !restoreTarget.id) return;
    setIsRestoring(true);
    try {
      await restoreProject(token, restoreTarget.id);
      setProjectsData(prev => prev.filter(p => p.id !== restoreTarget.id));
      toast.success('Project restored successfully');
      await refreshProjects();
      setRestoreTarget(null);
    } catch (e) {
      const status = e instanceof Error && 'status' in e ? (e as { status: number }).status : undefined;
      if (status === 403) {
        toast.error('You do not have permission to restore this project');
      } else {
        toast.error('Failed to restore project');
      }
      console.error('Restore error:', e);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCloned = (newProjectId: string) => {
    setCloneTarget(null);
    setIsCloneModalOpen(false);
    navigate(`/projects/${newProjectId}`);
  };

  const renderRowActions = (project: ProjectInterface) => (
    <div className="flex gap-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setCloneTarget(project);
          setIsCloneModalOpen(true);
        }}
        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
      >
        Clone
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setRestoreTarget(project);
        }}
        className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
      >
        Restore
      </button>
    </div>
  );

  // Risk filtering happens on the backend, so the loaded list is already the filtered list.
  const filteredProjects = projectsData;

  if (accessLoading || (hasAccess && isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner
          text="Loading archived projects..."
          className="text-center"
        />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="max-w-md w-full mx-4 bg-white rounded-lg border border-[#eaeaea] p-6">
          <div className="flex items-start gap-3">
            <FaLock className="text-red-600 w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-red-900 mb-2">Access Denied</h2>
              <p className="text-sm text-red-700">You don't have access to archived projects.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#888] mb-4">Failed to load archived projects</p>
          <button
            onClick={() => loadProjects(sortOrder)}
            className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardFilters
        setShowMenu={setShowMenu}
        showMenu={showMenu}
        view={'projects'}
        onSortChange={handleSortChange}
        onPriorityFilter={handleRiskFilterChange}
        selectedRiskLevel={riskLevelFilter}
      />
      <DashboardList
        servers={filteredProjects}
        clickable={true}
        emptyMessage="No archived projects found"
        renderRowActions={renderRowActions}
        linkTo={(project) => `/projects/${project.id}?archived=true`}
      />

      <ConfirmationDialog
        isOpen={!!restoreTarget}
        title="Restore Project"
        message="This project will move back to your active projects list."
        confirmText="Restore"
        onConfirm={handleRestore}
        onClose={() => setRestoreTarget(null)}
        isLoading={isRestoring}
      />

      <CloneProjectModal
        isOpen={isCloneModalOpen}
        onClose={() => {
          setIsCloneModalOpen(false);
          setCloneTarget(null);
        }}
        sourceProject={cloneTarget}
        onCloned={handleCloned}
      />
    </>
  );
};
